import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { MdAdd, MdChevronRight } from 'react-icons/md';
import FeaturedCampaignCarousel from './FeaturedCampaignCarousel';
import OngoingCampaignsCarousel from './OngoingCampaignsCarousel';
import ScheduledCampaignsCarousel from './ScheduledCampaignsCarousel';
import TopTrendingCampaigns from './TopTrendingCampaigns';
import CreateCampaignDrawer from '../campaingIdeas/CreateCampaignDrawer';
import { fetchScheduledCampaignsRequest } from '../../redux/slices/scheduledCampaignsSlice';
import { fetchReviewAnalysisRequest } from '../../redux/slices/reviewAnalysisSlice';
import { fetchRestaurantRequest } from '../../redux/slices/restaurantSlice';
import { fetchSocialMediaPostsRequest } from '../../redux/slices/socialMediaPostsSlice';
import { fetchGeneratedIdeasRequest } from '../../redux/slices/generatedIdeasSlice';
import { triggerContentGenerationRequest } from '../../redux/slices/contentGenerationSlice';
import { calculateCampaignProgress, parseSocialMediaChannels } from '../../utils/campaignProgress';
import type { RootState } from '../../redux/store';
import type { FeaturedCampaign } from './types';
import { useRestaurantNav } from '../../hooks/useRestaurantNav';
import LoadingState from '../common/LoadingState';

/**
 * Dashboard Component
 * Displays featured campaign, demographics, and schedules
 * Renders inside MainLayout (sidebar is in the layout, not here)
 */
const Dashboard: React.FC = () => {
const { navigate } = useRestaurantNav();
  const dispatch = useDispatch();
  const [isCreateDrawerOpen, setIsCreateDrawerOpen] = useState(false);

  // Get restaurant ID from Redux
  const restaurantId = useSelector((state: RootState) => state.clover.restaurantId);

  // Get scheduled campaigns from Redux
  const { campaigns: scheduledCampaigns, loading: campaignsLoading } = useSelector(
    (state: RootState) => state.scheduledCampaigns
  );

  // Get review analysis from Redux
  const { data: reviewData } = useSelector((state: RootState) => state.reviewAnalysis);

  // Get restaurant data from Redux
  const { data: restaurantData } = useSelector((state: RootState) => state.restaurant);

  // Fetch scheduled campaigns, review analysis, restaurant data, and social media posts on mount
  useEffect(() => {
    if (!restaurantId) return;
    dispatch(fetchScheduledCampaignsRequest({ restaurantId }));
    dispatch(fetchReviewAnalysisRequest(restaurantId));
    dispatch(fetchRestaurantRequest());
    dispatch(fetchSocialMediaPostsRequest(restaurantId));
  }, [dispatch, restaurantId]);

  // Get social media posts from Redux
  const { posts: socialMediaPosts, loading: postsLoading } = useSelector(
    (state: RootState) => state.socialMediaPosts
  );

  console.log('Social Media Posts:', socialMediaPosts);

  // Calculate review counts from review analysis data
  const positiveCount = reviewData?.key_positive_themes?.length || 0;
  const negativeCount = reviewData?.key_improvement_themes?.length || 0;

  // Get content generation state
  const {
    loading: contentGenerating,
    flyerDetails,
    ideaId,
    ideaType,
  } = useSelector((state: RootState) => state.contentGeneration);

  // Navigate to flyer page when flyer details are loaded
  useEffect(() => {
    if (flyerDetails && !contentGenerating) {
      // Build URL with query parameters using ideaId and ideaType from Redux
      const params = new URLSearchParams();
      if (ideaId) params.append('ideaId', ideaId.toString());
      if (ideaType) params.append('ideaType', ideaType);
      
      const url = params.toString() ? `/flyer?${params.toString()}` : '/flyer';
      navigate(url);
    }
  }, [flyerDetails, contentGenerating, ideaId, ideaType, navigate]);

  // Handler for Use Idea button
  const handleUseIdea = (ideaId: number, ideaType: string) => {
    if (!restaurantId) return;
    
    // Build payload with idea_id, idea_type, and task_type
    const payload = {
      idea_id: ideaId,
      idea_type: ideaType,
      idea: "",
      task_type: "SOCIAL_MEDIA",
      campaign_goal: "",
      quarterly_budget: "",
      weekly_promotional_goal: "",
      blog_post_goal: "",
      target_keyword: ""
    };
    
    dispatch(triggerContentGenerationRequest({ restaurantId, payload }));
  };

  // Featured Campaigns Data from generated_ideas API
  const featuredCampaigns: FeaturedCampaign[] = [
    {
      id: 7,
      title: "Your christmas Miracle comes early",
      description: [
        "2025-12-15", // created_at date
        "Increase Revenue & Profitability"
      ],
      image: 'https://snapmenu-restaurant-workers.raghavan-7fc.workers.dev/files/1770020177820-generated_flyer.jpeg',
      buttonPrimary: {
        text: 'Use Idea',
        onClick: () => handleUseIdea(7, "GENERAL_IDEA")
      }
    },
    {
      id: 8,
      title: "A Taste of the Winter Games.'",
      description: [
        "2025-12-15", // created_at date
        "Attract New Customers"
      ],
      image: 'https://snapmenu-restaurant-workers.raghavan-7fc.workers.dev/files/1770054763058-generated_flyer.jpeg',
      buttonPrimary: {
        text: 'Use Idea',
        onClick: () => handleUseIdea(8, "GENERAL_IDEA")
      }
    },
    {
      id: 9,
      title: "Your Pizza Is Now A Cocktail.",
      description: [
        "2025-12-15", // created_at date
        "Build Brand & Competitive Advantage"
      ],
      image: 'https://snapmenu-restaurant-workers.raghavan-7fc.workers.dev/files/1770032306070-generated_flyer.jpeg',
      buttonPrimary: {
        text: 'Use Idea',
        onClick: () => handleUseIdea(9, "GENERAL_IDEA")
      }
    }
  ];

  // Transform scheduled campaigns API data to match OngoingCampaign interface
  const backgroundColors = ['#C4613B', '#B8984C', '#7A9B9E', '#4A5568', '#8B5A3C', '#6B7280'];
  
  const ongoingCampaigns = scheduledCampaigns
    .filter(campaign => campaign.flyer_info && campaign.flyer_info.flyer_url) // Only show campaigns with flyer images
    .map((campaign, index) => {
      const progress = calculateCampaignProgress(campaign.start_date, campaign.end_date);
      const socialMediaChannels = parseSocialMediaChannels(campaign.social_media);
      
      // Check if campaign is completed (progress is 100 or end date has passed)
      const now = new Date();
      const endDate = new Date(campaign.end_date);
      const isCompleted = progress >= 100 || now > endDate;
      
      return {
        id: campaign.id,
        name: campaign.generated_content.idea_title,
        role: `${campaign.start_date} - ${campaign.end_date}`,
        company: socialMediaChannels.join(', '),
        image: campaign.flyer_info.flyer_url,
        progress: progress,
        backgroundColor: backgroundColors[index % backgroundColors.length],
        socialMedia: socialMediaChannels,
        startDate: campaign.start_date,
        endDate: campaign.end_date,
        completed: isCompleted
      };
    });

  // Show loading state when generating content
  if (contentGenerating) return <LoadingState message="Generating content..." />;

  return (
    <>
        {/* Create Campaign Drawer */}
        <CreateCampaignDrawer isOpen={isCreateDrawerOpen} onClose={() => setIsCreateDrawerOpen(false)} />

        {/* Animation Styles */}
        <style>{`
          @keyframes shine {
            0% {
              left: -100%;
            }
            100% {
              left: 160%;
            }
          }
          
          @keyframes marquee {
            0% {
              transform: translateX(0%);
            }
            100% {
              transform: translateX(-100%);
            }
          }
          
          .shine-button {
            position: relative;
            overflow: hidden;
          }
          
          .shine-button::before {
            content: '';
            position: absolute;
            top: 0;
            left: -100%;
            width: 50%;
            height: 100%;
            background: linear-gradient(
              90deg,
              transparent,
              rgba(255, 255, 255, 0.3),
              rgba(255, 255, 255, 0.5),
              rgba(255, 255, 255, 0.3),
              transparent
            );
            animation: shine 3s infinite;
            transform: skewX(-25deg);
          }
        `}</style>

        {/* Header Section */}
        <div 
          className="flex items-center justify-between mb-8 rounded-[20px]"
          style={{
            padding: '10px 20px 10px 20px',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 0 14px 0 rgba(0, 0, 0, 0.1)',
            background: 'linear-gradient(90deg, rgba(33, 33, 33, 0.92), rgba(55, 55, 55, 0.92))',
            opacity: 1,
            border: "1px solid #e0e0e021"
          }}
        >
          {/* Left Side: Logo and Welcome */}
          <div className="flex items-center gap-4">
            <div className='flex justify-center items-center gap-3'>
              {restaurantData?.logo && (
                <img className="w-auto h-10" src={restaurantData.logo} alt="logo" />
              )}
              <h1 className="text-xl font-medium text-white">
                Welcome Back, <span className="text-[#EE4423] font-semibold">{restaurantData?.restaurant_name || 'Guest'}</span>!
              </h1>
            </div>
          </div>
          
          {/* Right Side: Review Analysis + Create Campaign Button */}
          <div className="flex items-center gap-4">
            {/* Review Analysis Section - Only show if there's data */}
            {(positiveCount > 0 || negativeCount > 0) && (
              <div className="flex items-center gap-4 px-6 py-3 rounded-full bg-[#212121] overflow-hidden"
              style={{ border: "1px solid rgba(224, 224, 224, 0.13)", maxWidth: '300px'}}
              >
                <div className="w-24 overflow-hidden">
                  <div className=" whitespace-nowrap">
                    <span className="text-gray-300 text-xs font-normal">Review Analysis</span>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  {/* Thumbs Up */}
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M2 10.5a1.5 1.5 0 113 0v6a1.5 1.5 0 01-3 0v-6zM6 10.333v5.43a2 2 0 001.106 1.79l.05.025A4 4 0 008.943 18h5.416a2 2 0 001.962-1.608l1.2-6A2 2 0 0015.56 8H12V4a2 2 0 00-2-2 1 1 0 00-1 1v.667a4 4 0 01-.8 2.4L6.8 7.933a4 4 0 00-.8 2.4z" />
                    </svg>
                    <span className="text-white font-semibold text-lg">{positiveCount}</span>
                  </div>
                  {/* Thumbs Down */}
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M18 9.5a1.5 1.5 0 11-3 0v-6a1.5 1.5 0 013 0v6zM14 9.667v-5.43a2 2 0 00-1.105-1.79l-.05-.025A4 4 0 0011.055 2H5.64a2 2 0 00-1.962 1.608l-1.2 6A2 2 0 004.44 12H8v4a2 2 0 002 2 1 1 0 001-1v-.667a4 4 0 01.8-2.4l1.4-1.866a4 4 0 00.8-2.4z" />
                    </svg>
                    <span className="text-white font-semibold text-lg">{negativeCount}</span>
                  </div>
                </div>
              </div>
            )}
            
            {/* Create Campaign Button */}
            <motion.button
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setIsCreateDrawerOpen(true)}
              className="shine-button bg-[#EE4423] text-white font-medium cursor-pointer py-3 px-5 rounded-full flex items-center gap-2  transition-all"
            >
              <MdAdd size={24} />
              Create Campaign
            </motion.button>
          </div>
        </div>

        {/* Main Grid Layout */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-8">
          {/* Featured Campaign Carousel - Takes 1 column (50%) */}
          <div>
            {/* Card Header with Title and Explore More Button */}
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-xl font-medium text-[#EE4423]">Four Big Ideas</h2>
              <div className="flex items-center gap-3">
                {/* Navigation Arrows */}
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      const swiperEl = document.querySelector('.featured-campaign-carousel .swiper') as any;
                      swiperEl?.swiper?.slidePrev();
                    }}
                    className="w-8 h-8 rounded-full bg-gray-800 hover:bg-gray-700 flex items-center justify-center transition-colors border border-gray-700"
                  >
                    <MdChevronRight className="text-white rotate-180" size={16} />
                  </button>
                  <button
                    onClick={() => {
                      const swiperEl = document.querySelector('.featured-campaign-carousel .swiper') as any;
                      swiperEl?.swiper?.slideNext();
                    }}
                    className="w-8 h-8 rounded-full bg-white hover:bg-gray-100 flex items-center justify-center transition-colors"
                  >
                    <MdChevronRight className="text-black" size={16} />
                  </button>
                </div>

                <button 
                  onClick={() => navigate('/campaigns')}
                  className="px-2 cursor-pointer py- flex gap-1 items-center text-gray-400 hover:text-white rounded-full font-normal text-sm transition-colors"
                >
                  Explore More
                  <MdChevronRight />
                </button>
              </div>
            </div>
            
            {/* Carousel */}
            <div className="h-[350px]">
              {featuredCampaigns.length > 0 ? (
                <FeaturedCampaignCarousel 
                  campaigns={featuredCampaigns}
                  config={{
                    autoplay: true,
                    autoplayDelay: 5000,
                    loop: true,
                    speed: 800,
                    effect: 'coverflow',
                    navigation: false,
                    pagination: false
                  }}
                />
              ) : (
                <div className="h-full flex items-center justify-center bg-[#212121] rounded-xl border border-gray-700">
                  <div className="text-center p-8">
                    <p className="text-gray-400 text-sm mb-4">Seems the weather is bad, please regenerate</p>
                    <button 
                      onClick={() => navigate('/campaigns')}
                      className="px-4 py-2 bg-[#EE4423] hover:bg-[#d63d20] text-white rounded-full text-sm transition-colors"
                    >
                      View Campaign Ideas
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
          {/* Top Trending Campaigns - Takes 1 column (50%) */}
          <div>
            {/* Top Trending Campaigns Component */}
            <TopTrendingCampaigns />
          </div>
        </div>

        {/* Ongoing Campaigns Carousel */}
        {/* <OngoingCampaignsCarousel campaigns={ongoingCampaigns} /> */}

        {/* Scheduled Campaigns Carousel */}
        <ScheduledCampaignsCarousel />
    </>
  );
};

export default Dashboard;
